import React, { useState } from 'react'

import { storefrontFetch } from '../misc/fetch_helpers'
import CurrencyUSD from '../misc/currency_formats'
import Spinner from '../misc/spinner'
import { Modal } from 'lensrentals-components'
import KeeperTestDrive from '../../assets/images/keeper-test-drive-icon.png'
import KeeperCertified from '../../assets/images/keeper-certified-icon.png'

import styles from '../../assets/stylesheets/pages/product/purchasing_button_modal.module.scss'

function PurchasingButtonModal(props) {
  const {
    productId,
    saleProductUrl,
    hasSaleProduct,
    buyNowPriceRange
  } = props

  const keeperTestDrive = !!buyNowPriceRange
  const keeperPurchase = !!hasSaleProduct

  const [showModal, setShowModal] = useState(false)

  const [buyNowInfoData, setBuyNowInfoData] = useState({
    loading: true,
    error: false,
    data: null
  })

  const [saleListingPriceData, setSaleListingPriceData] = useState({
    loading: true,
    error: false,
    data: null
  })

  const setError = (setter) => {
    setter(() => ({
      data: null,
      error: true,
      loading: false,
    }))
  }

  function getBuyNowInfoData(response) {
    try {
      const buyNowPriceRange = response.data.products.edges[0].node.buyNowPriceRange
      if (buyNowPriceRange) {
        const [lowPrice, highPrice] = buyNowPriceRange.sort((a, b) => a - b) // low to high
        const buyNowInfo = { lowPrice, highPrice }

        setBuyNowInfoData(() => ({
          data: buyNowInfo,
          error: false,
          loading: false,
        }))
      } else {
        setError(setBuyNowInfoData)
      }
    } catch (error) {
      console.log('error fetching Keeper Test Drive estimate', error)

      setError(setBuyNowInfoData)
    }
  }

  function getSaleListingPriceData(response) {
    try {
      const saleListings = response.data.products.edges[0].node.saleProduct.saleListings
      const cheapestPrice = saleListings.sort((a, b) => a.price - b.price)[0].price

      if (cheapestPrice) {
        setSaleListingPriceData(() => ({
          data: cheapestPrice,
          error: false,
          loading: false,
        }))
      } else {
        setError(setSaleListingPriceData)
      }
    } catch (error) {
      console.log('error fetching Keeper Purchase estimate', error)

      setError(setSaleListingPriceData)
    }
  }

  async function getKeeperPrices() {
    try {
      const response = await storefrontFetch(`/products/${productId}/purchase_price.json`, { method: 'get' })
      if (keeperTestDrive && !buyNowInfoData.data) getBuyNowInfoData(response)
      if (keeperPurchase && !saleListingPriceData.data) getSaleListingPriceData(response)
    } catch (error) {
      console.log('error fetching Keeper estimate', error)

      setError(setBuyNowInfoData)
      setError(setSaleListingPriceData)
    }
  }

  function handleShowModal() {
    getKeeperPrices()
    setShowModal(true)
  }

  function handleHideModal() {
    setShowModal(false)
  }

  function toggleModal() {
    showModal ? handleHideModal() : handleShowModal()
  }

  const Loading = ({ isLoading, hasError, children }) => {
    if (hasError) {
      return (
        <div className='price-estimate-error'>
          Uh oh, something went wrong fetching the estimate. Please try again or <a href='/contact'>contact us</a>.
        </div>
      )
    }

    if (isLoading) {
      return (
        <div className={styles['price-estimate-loader']}>
          <Spinner
            loading={true}
            padding={0}
            overlayOnChildren={false}
          >
            Loading Price Estimate...
          </Spinner>
        </div>
      )
    }

    return children
  }

  return (
    <>
      <button
        className='button get-estimate'
        onClick={handleShowModal}
      >
        Learn More
      </button>

      <Modal
        id='purchasing-modal'
        size='medium'
        show={showModal}
        toggleShow={toggleModal}
      >
        <Modal.Title>
          <h2>Lensrentals Keeper Programs</h2>
        </Modal.Title>

        <Modal.Content>
          <div className={styles['purchase-button-modal']}>
            <section className='keeper-test-drive'>
              <header>
                Keeper Test Drive
                <em>
                  Try it, then buy it.
                </em>
              </header>

              <img src={KeeperTestDrive} />

              <p>Keeper Test Drive is now one of two ways to buy from Lensrentals! Take gear for a test drive rental and if you love it, keep it. We’ll even take up to 7 days of rental fees off the purchase price.</p>

              {
                keeperTestDrive
                  ? (
                    <>
                      <div className={styles['estimate-price']}>
                        <Loading isLoading={buyNowInfoData.loading} hasError={buyNowInfoData.error}>
                        <strong><CurrencyUSD amount={buyNowInfoData.data?.lowPrice} /></strong> - <strong><CurrencyUSD amount={buyNowInfoData.data?.highPrice} /></strong>
                        </Loading>
                      </div>
                      <footer>
                        <a href='/keeper' className='purchasing-learn-more'>Learn More</a>
                        <aside className='purchase-price-info'>
                          Purchase price varies based on age of rental equipment.
                        </aside>
                      </footer>
                    </>
                  )
                  : (
                    <footer className={styles['not-available']}>
                      <span>Not available for <strong>Keeper Test Drive</strong> at this time.</span>
                      <em>Please check back or <a href='/contact'>contact us</a>!</em>
                    </footer>
                  )
              }
            </section>
            <section className='keeper-purchase'>
              <header>
                Keeper Purchase
                <em>
                  The highest standard for used gear.
                </em>
              </header>

              <img src={KeeperCertified} />

              <p>
                Already know you want it? Browse through our list of Keeper copies. Each copy is tested, graded, and listed for sale at great prices.
              </p>

              {
                keeperPurchase
                  ? (
                    <>
                      <div className={styles['estimate-price']}>
                        <Loading isLoading={saleListingPriceData.loading} hasError={saleListingPriceData.error}>
                          <strong><CurrencyUSD amount={saleListingPriceData.data} /></strong>
                          <span>&nbsp;/ Estimate</span>
                        </Loading>
                      </div>
                      <footer>
                        <a href={saleProductUrl} className='purchasing-learn-more'>Learn More</a>
                        <aside className='purchase-price-info'>
                          Purchase price varies based on age of rental equipment.
                        </aside>
                      </footer>
                    </>
                  )
                  : (
                    <footer className={styles['not-available']}>
                      <span>Not available for <strong>Keeper</strong> at this time.</span>
                      <em>Please check back or <a href='/contact'>contact us</a>!</em>
                    </footer>
                  )
              }
            </section>
          </div>
        </Modal.Content>
      </Modal>
    </>
  )
}

export default PurchasingButtonModal
